import { FilledButton, OutlinedButton } from '@/components/UI/Button';
import { Box, IconButton, styled, Typography } from '@mui/material';

export const FormContainer = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 400px',
  gap: '48px',
  alignItems: 'start',
  marginTop: '8px',

  [theme.breakpoints.down('lg')]: {
    gridTemplateColumns: '1fr',
  },
}));

export const CardsColumn = styled(Box)(() => ({
  minWidth: 0,
}));

export const TitleContainer = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
}));

export const PageTitle = styled(Typography)(() => ({
  color: '#1A1A1A',
  fontSize: '24px',
  fontWeight: 600,
  lineHeight: '140%',
  textTransform: 'uppercase',
}));

export const PageSubtitle = styled(Typography)(() => ({
  color: '#475467',
  fontSize: '14px',
  lineHeight: '140%',
  marginTop: '4px',
}));

export const NameHelper = styled(Typography)(() => ({
  color: '#475467',
  fontSize: '12px',
  lineHeight: '140%',
  margin: '4px 0 0 4px',
}));

export const CardsHeader = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  margin: '32px 0 16px',
}));

export const CardsTitle = styled(Typography)(({ theme }) => ({
  color: '#1A1A1A',
  fontSize: '16px',
  fontWeight: 600,

  '& span': {
    color: theme.palette.secondary.main,
    fontSize: '13px',
    fontWeight: 500,
    marginLeft: '12px',
  },
}));

export const AddCardButton = styled(FilledButton)(() => ({
  width: 'fit-content',
  padding: '10px 32px',
}));

interface CardBoxProps {
  isDragging?: boolean;
  isDragOver?: boolean;
}

export const CardBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isDragging' && prop !== 'isDragOver',
})<CardBoxProps>(({ theme, isDragging, isDragOver }) => ({
  backgroundColor: theme.palette.common.white,
  border: '1px solid #D0D5DD',
  borderRadius: '12px',
  padding: '24px',
  marginBottom: '24px',
  cursor: 'grab',
  boxShadow: '0 1px 3px rgba(16, 24, 40, 0.08)',
  transition:
    'transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease, opacity 0.2s ease',

  '&:active': {
    cursor: 'grabbing',
  },

  ...(isDragging && {
    position: 'relative',
    zIndex: 2,
    opacity: 0.85,
    transform: 'scale(1.03)',
    borderColor: theme.palette.secondary.main,
    boxShadow: '0 16px 32px rgba(0, 0, 0, 0.18)',
  }),

  ...(isDragOver &&
    !isDragging && {
      borderColor: theme.palette.secondary.main,
      borderStyle: 'dashed',
      borderWidth: '2px',
      backgroundColor: `${theme.palette.secondary.main}0D`,
    }),
}));

export const CardHeader = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  cursor: 'pointer',

  '& h3': {
    color: '#1A1A1A',
    fontSize: '18px',
    fontWeight: 600,
  },
}));

export const CardHeaderTitle = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
}));

export const CardHeaderActions = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
}));

export const CollapseButton = styled(IconButton)(() => ({
  color: '#475467',

  '& span': {
    fontSize: '22px',
  },
}));

export const CardBody = styled(Box)(() => ({
  paddingTop: '16px',
}));

export const DeleteCardButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.secondary.main,

  '& span': {
    fontSize: '20px',
  },
}));

export const OptionalLabel = styled(Typography)(() => ({
  color: '#1A1A1A',
  fontSize: '14px',
  fontWeight: 600,
  marginTop: '16px',
}));

export const OptionalHelper = styled(Typography)(() => ({
  color: '#475467',
  fontSize: '12px',
  lineHeight: '140%',
  marginBottom: '12px',
}));

export const MediaContainer = styled(Box)(() => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '16px',
  alignItems: 'center',
}));

export const ImageUploadButton = styled(OutlinedButton)(() => ({
  border: '1px solid #C4C4C4',
  color: '#475467',
  borderRadius: '16px',
  width: '100%',
  height: '56px',
  fontSize: '14px',
  fontWeight: 400,
  display: 'flex',
  gap: '8px',

  '& span': {
    fontSize: '18px',
  },

  '&:hover': {
    backgroundColor: '#F5F5F4',
    color: '#475467',
  },

  '&:disabled': {
    color: '#475467',
  },
}));

export const ImagePreviewBox = styled(Box)(() => ({
  position: 'relative',
  width: '240px',
  maxWidth: '100%',
  aspectRatio: '16 / 9',
  marginBottom: '12px',
  borderRadius: '8px',
  overflow: 'hidden',

  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },
}));

export const VideoPreviewBox = styled(Box)(() => ({
  position: 'relative',
  width: 'fit-content',
  maxWidth: '100%',
  marginTop: '16px',
  marginBottom: '12px',
  borderRadius: '8px',
  overflow: 'hidden',

  '& img': {
    maxWidth: '240px',
    maxHeight: '160px',
    display: 'block',
  },

  '& .play-icon': {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    color: '#FFFFFF',
    opacity: 0.9,
    pointerEvents: 'none',

    '& span': {
      fontSize: '36px',
    },
  },
}));

export const RemoveImageButton = styled(IconButton)(() => ({
  position: 'absolute',
  top: '4px',
  right: '4px',
  backgroundColor: '#1A1A1A',
  color: '#FFFFFF',
  width: '24px',
  height: '24px',

  '& span': {
    fontSize: '14px',
  },

  '&:hover': {
    backgroundColor: '#1A1A1A',
    filter: 'brightness(150%)',
  },
}));

export const SideContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  position: 'sticky',
  top: '24px',
}));

export const SidePreviewButton = styled(FilledButton)(({ theme }) => ({
  backgroundColor: theme.palette.secondary.main,
  width: '100%',
  display: 'flex',
  gap: '8px',

  '& span': {
    fontSize: '20px',
  },

  '&:hover': {
    backgroundColor: theme.palette.primary.main,
  },
}));

export const SideButtonsRow = styled(Box)(() => ({
  display: 'flex',
  gap: '10px',
}));

export const DraftButton = styled(OutlinedButton)(({ theme }) => ({
  width: 'auto',
  minWidth: 'fit-content',
  flex: 1,
  padding: '10px 24px',
  fontSize: '14px',
  border: `1px solid ${theme.palette.primary.main}`,
  color: theme.palette.primary.main,
  whiteSpace: 'nowrap',
  textTransform: 'none',

  '&:disabled': {
    color: theme.palette.primary.main,
  },
}));

export const PublishButton = styled(FilledButton)(() => ({
  width: 'auto',
  minWidth: 'fit-content',
  flex: 1,
  padding: '10px 24px',
  fontSize: '14px',
  whiteSpace: 'nowrap',
}));

export const TipsBox = styled(Box)(() => ({
  backgroundColor: '#E9FBF8',
  borderRadius: '8px',
  padding: '16px 24px',
  color: '#1A1A1A',
  fontSize: '13px',
  lineHeight: '160%',

  '& strong': {
    display: 'block',
    marginBottom: '4px',
  },

  '& ul': {
    margin: 0,
    paddingLeft: '18px',
    color: '#475467',
  },
}));
